/* eslint-disable sonarjs/no-duplicate-string */

/* cSpell:disable */
import React, { CSSProperties } from 'react';

import CurrencyInput from 'react-currency-input-field';

import { FormatHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import {
	Box,
	Button,
	ComposedElement,
	Spacer,
	TextBlock,
	Typography,
	useTheme,
} from '@bcp-nextgen-dx-component-factory/design-system';
import { EditNew } from '@bcp-nextgen-dx-component-factory/icons-foundation';
import { useGetCurrentBreakpoint } from '@hooks';

import { SelectValueProps } from './selectValue';
import { useSelectValue } from './selectValue.helper';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const SelectValueAb: React.FC<SelectValueProps> = (props: SelectValueProps) => {
	const { defaultValues, value, minValue, maxValue } = props;
	const { currentView } = useGetCurrentBreakpoint();
	const { colorPalette } = useTheme();

	const {
		editingCustomValue,
		lastEditedValue,
		isValidCustomValue,
		handleOnChangeValue,
		handleSetCustomValue,
		handleCompleteEditing,
		handleCustomInputChange,
		isCustomValue,
		handleOnEnterKey,
		handleMobileCompleteEditing,
		handleShowCustomValueErrorMessage,
		hideCustomValueErrorMessage,
		translate,
	} = useSelectValue(props);

	const getCardBorder = () => `2px solid ${handleShowCustomValueErrorMessage() ? '#D0021B' : '#00B9FF'}`;

	const styles = {
		smallView: {
			editingCustomValueArea: {
				mainContainer: {
					justifyContent: 'center',
					border: getCardBorder(),
					borderRadius: '8px',
					cursor: 'pointer',
					padding: '9px 6px',
					background: '#FFFFFF',
				},
				currencyInput: {
					fontSize: '1.38rem',
					color: '#000000',
					fontWeight: '700',
					fontFamily: "'TT Chocolates ActivoBank',sans-serif",
					border: 'none',
					padding: 0,
					height: '44px',
					outline: 'none',
					width: `${value.toString().length + (value >= 1000 ? 1 : 0)}ch`,
				},
			},

			notEditingCustomValueArea: {
				card: {
					width: 'calc(50% - 12px)',
				},
			},
		},
		tabletView: {
			editingCustomValueArea: {
				mainContainer: {
					justifyContent: 'center',
					columnGap: '4px',
					border: getCardBorder(),
					borderRadius: '8px',
					cursor: 'pointer',
					padding: '9px 6px',
					background: '#FFFFFF',
				},
				currencyInput: {
					fontSize: '1.38rem',
					color: '#000000',
					fontWeight: '700',
					fontFamily: "'TT Chocolates ActivoBank',sans-serif",
					border: 'none',
					padding: 0,
					height: '44px',
					outline: 'none',
					width: `${value.toString().length + 1}ch`,
				},
			},
			notEditingCustomValueArea: {
				card: {
					width: 'calc(33% - 17px)',
				},
			},
		},
		bigView: {
			editingCustomValueArea: {
				mainContainer: {
					justifyContent: 'center',
					border: `2px solid ${handleShowCustomValueErrorMessage() ? '#D0021B' : '#00B9FF'}`,
					borderRadius: '8px',
					cursor: 'pointer',
					padding: '9px 6px',
					background: '#FFFFFF',
					height: '64px',
					columnGap: '12px',
				},

				currencyInput: {
					fontSize: '1.38rem',
					color: '#000000',
					fontWeight: '700',
					fontFamily: "'TT Chocolates ActivoBank',sans-serif",
					border: 'none',
					textAlign: 'right',
					padding: 0,
					height: '44px',
					outline: 'none',
					width: value >= 1000 ? `${value.toString().length + 1}ch` : `${value.toString().length}ch`,
				},
			},
			notEditingCustomValueArea: {
				card: {
					width: 'calc(33% - 17px)',
				},
			},
		},
	};

	const inputView = () => (
		<>
			<CurrencyInput
				onValueChange={(e, a, value) => handleCustomInputChange(value?.float || 0)}
				onKeyDown={handleOnEnterKey}
				onBlur={handleMobileCompleteEditing}
				onFocus={hideCustomValueErrorMessage}
				value={value}
				maxLength={maxValue.toString().length}
				allowDecimals={false}
				autoFocus={true}
				groupSeparator={'.'}
				decimalSeparator=","
				style={styles[currentView].editingCustomValueArea.currencyInput as CSSProperties}
			/>
		</>
	);

	return (
		<>
			<Box
				styles={{
					width: '100%',
					padding: '20px 0',
					justifyContent: 'space-between',
					gap: currentView === 'smallView' ? '16px 15px' : '27px 20px',
					flexWrap: 'wrap',
				}}
			>
				{defaultValues?.length &&
					defaultValues.map((currentValue, i) => {
						const selected = value === currentValue;
						return (
							<Box
								key={i}
								styles={{
									justifyContent: 'center',
									alignItems: 'baseline',
									border: selected ? '2px solid #00B9FF' : '2px solid #abb4c4',
									borderRadius: '8px',
									cursor: 'pointer',
									height: '64px',
									padding: '15px 0',
									background: '#FFFFFF',
									...styles[currentView].notEditingCustomValueArea.card,
								}}
								onPress={() => handleOnChangeValue(currentValue)}
							>
								<Typography
									appearance={currentView === 'smallView' ? 'subtitle-100-03' : 'headline-75-03'}
									colorToken={selected ? 'link-default-light' : 'primary-light'}
								>
									{FormatHelper.formatNumber(currentValue, 'noformat', 0)}
								</Typography>
								<Spacer horizontal={'3px'} />
								<Box
									styles={{
										justifyContent: 'center',
										flexDirection: 'column',
									}}
								>
									<Typography
										appearance="body-100-02"
										colorToken={selected ? 'link-default-light' : 'primary-light'}
									>
										€
									</Typography>
								</Box>
							</Box>
						);
					})}

				<Box styles={{ flexDirection: 'column', ...styles[currentView].notEditingCustomValueArea.card }}>
					{!editingCustomValue && (
						<Box
							styles={{
								justifyContent: 'center',
								border: isCustomValue ? '2px solid #00B9FF' : '2px solid #abb4c4',
								borderRadius: '8px',
								cursor: 'pointer',
								padding: '15px 0',
								background: '#FFFFFF',
								height: '64px',
							}}
							onPress={() => handleSetCustomValue()}
						>
							{isValidCustomValue && (
								<>
									<Typography
										appearance="headline-75-03"
										colorToken={isCustomValue ? 'link-default-light' : 'primary-light'}
									>
										{lastEditedValue}
									</Typography>
									<Spacer horizontal={'3px'} />
									<Box
										styles={{
											justifyContent: 'end',
											flexDirection: 'column',
											marginRight: '8px',
										}}
									>
										<Typography
											appearance="body-100-02"
											colorToken={isCustomValue ? 'link-default-light' : 'primary-light'}
										>
											€
										</Typography>
									</Box>
									<Spacer horizontal={'2px'} />
									<Box
										styles={{
											justifyContent: 'center',
											flexDirection: 'column',
											marginTop: '-4px',
										}}
									>
										<EditNew.Outline size={'xSmall'} fill={'#00B9FF'} />
									</Box>
								</>
							)}
							{!isValidCustomValue && (
								<Box styles={{ alignItems: 'center' }}>
									<TextBlock.Currency
										size="small"
										hideCents
										floatValue={0}
										variant="neutral"
										fontWeight="extraStrong"
									/>
									<Box styles={{ margin: '4px 8px 0 0' }}>
										<Typography appearance="body-100-02">€</Typography>
									</Box>
									<Box styles={{ marginTop: '-4px' }}>
										<ComposedElement
											icon={{
												source: EditNew.Outline,
												svgProps: { fill: colorPalette.color_01[500] },
											}}
											size="xSmall"
										/>
									</Box>
								</Box>
							)}
						</Box>
					)}
					{editingCustomValue && (
						<Box styles={styles[currentView].editingCustomValueArea.mainContainer}>
							{currentView === 'bigView' ? <Box>{inputView()}</Box> : inputView()}

							{currentView === 'bigView' && (
								<Box
									styles={{
										flexDirection: 'column',
										justifyContent: 'center',
									}}
								>
									<Box styles={{ width: '100%', height: '30px', justifyContent: 'center' }}>
										<Button
											onPress={handleCompleteEditing}
											disabled={!isValidCustomValue}
											size={'small'}
										>
											Ok
										</Button>
									</Box>
								</Box>
							)}
						</Box>
					)}
					{handleShowCustomValueErrorMessage() && (
						<Box styles={{ color: '#D0021B', marginTop: '7px' }}>
							{translate('simulator.dataInput.enterAmountBetweenXandY', {
								minAmount: minValue,
								maxAmount: maxValue,
							})}
						</Box>
					)}
				</Box>
			</Box>
		</>
	);
};
/* cSpell:enable */
